import { parseCookie } from '@/util/cookie.util';

export const getAuthorizationHeader = (): object => {
  const cookies = parseCookie(document.cookie);
  return cookies.accessToken ? { authorization: cookies.accessToken } : {};
};
export const getLanguageHeader = (): object => {
  const cookies = parseCookie(document.cookie);
  return cookies.lang ? { lang: cookies.lang } : { lang: 'en' };
};
export const getCMSHeader = (): object => {
  return { cms: `${process.env.ENV_TYPE}-cms` };
};

class PutRequest extends Request {
  constructor(
    url: string,
    body: object,
    formData: boolean = false,
    headers: { [index: string]: string } = {},
  ) {
    super(url, {
      method: 'PUT',
      body: JSON.stringify(body),
      headers: formData
        ? {
            'Content-Type': 'application/x-www-form-urlencoded',
            ...getAuthorizationHeader(),
            ...getCMSHeader(),
            ...getLanguageHeader(),
            ...headers,
          }
        : {
            'Content-Type': 'application/json',
            ...getAuthorizationHeader(),
            ...getCMSHeader(),
            ...getLanguageHeader(),
            ...headers,
          },
    });
  }
}

class PostRequest extends Request {
  constructor(
    url: string,
    body: object | FormData,
    formData: boolean = false,
    headers: { [index: string]: string } = {},
  ) {
    super(url, {
      method: 'POST',
      body: formData ? (body as FormData) : JSON.stringify(body),
      headers: formData
        ? {
            ...getAuthorizationHeader(),
            ...getCMSHeader(),
            ...getLanguageHeader(),
            ...headers,
          }
        : {
            'Content-Type': 'application/json',
            ...getAuthorizationHeader(),
            ...getCMSHeader(),
            ...getLanguageHeader(),
            ...headers,
          },
    });
  }
}

class GetRequest extends Request {
  constructor(url: string) {
    super(url, {
      method: 'GET',
      headers: {
        ...getAuthorizationHeader(),
        ...getCMSHeader(),
        ...getLanguageHeader(),
      },
    });
  }
}

class DeleteRequest extends Request {
  constructor(url: string) {
    super(url, {
      method: 'DELETE',
      headers: { ...getCMSHeader(), ...getAuthorizationHeader() },
    });
  }
}

export interface AjaxResponse<T> {
  status: number;
  data: T;
  message?: string;
}

const http = async (request: Request): Promise<Response> => {
  // request interceptors
  const response = await fetch(request);
  // response interceptors
  if (response.status === 500) {
    return new Response(null, { status: 500 });
  }
  if (response.status === 401) {
    document.location.href = '/#/login';
    return new Response(null, { status: 200 });
  }
  return response;
};

export class Ajax {
  async get<T>(url: string): Promise<AjaxResponse<T>> {
    const request = new GetRequest(url);
    const response = await http(request);

    const data = [200, 201].includes(response.status)
      ? await response.json()
      : undefined;

    return {
      status: response.status,
      data,
    };
  }

  async put<T>(
    url: string,
    body: object,
    formData?: boolean,
    headers?: { [index: string]: string },
  ): Promise<AjaxResponse<T>> {
    const request = new PutRequest(url, body, formData, headers);
    const response = await http(request);

    const json = await response.json();

    const data = [200, 201].includes(response.status) ? json : undefined;

    const message = data
      ? data.message
        ? [0]
        : undefined
      : Array.isArray(json?.message)
      ? json.message[0]
      : json.message || undefined;

    return {
      status: response.status,
      data,
      message,
    };
  }

  async post<T>(
    url: string,
    body: object,
    formData: boolean = false,
    headers?: { [index: string]: string },
  ): Promise<AjaxResponse<T>> {
    const request = new PostRequest(url, body, formData, headers);
    const response = await http(request);

    if (!response.ok) {
      return {
        status: response.status,
        data: null as unknown as T,
        message: await response.text(),
      };
    }

    const json = await response.json();

    const data = [200, 201].includes(response.status) ? json : undefined;

    const message = data
      ? data.message
        ? [0]
        : undefined
      : Array.isArray(json?.message)
      ? json.message[0]
      : json.message || undefined;

    return {
      status: response.status,
      data,
      message,
    };
  }

  async postComplex<T>(request: PostRequest): Promise<AjaxResponse<T>> {
    const response = await http(request);

    const data = [200, 201].includes(response.status)
      ? await response.json()
      : undefined;
    return {
      status: response.status,
      data,
    };
  }

  async delete<T>(url: string): Promise<AjaxResponse<T>> {
    const request = new DeleteRequest(url);
    const response = await http(request);

    const data = [200, 201].includes(response.status)
      ? await response.json()
      : undefined;
    return {
      status: response.status,
      data,
    };
  }
}
